export type CoatingDeckCoveringSection = {
  notApplicable: boolean
  percentCoatedStructureVisible: number | null
  paintCondition: string
  percentCorrosion: number | null
  corrosionLocalOrScattered: 'L' | 'S' | ''
  squareFootLocalCorrosion: number | null
  coveringDamaged: 'YES' | 'NO' | 'N/A' | ''
}

export type InsulationSection = {
  insulationPresent: boolean
  insulationDamaged: boolean
}

export class G1N6 {
  inspectorName: string
  organization: string
  contactInfo: string
  uic: string
  shipClass: string
  shipName: string
  hull: string
  compartmentNumber: string
  compartmentName: string
  swlin: string
  paintedSurfaceArea: number | null
  wc: string
  access: string
  apl: string
  rin: string
  configCode: string
  configSource: string
  date: string
  assessmentReason: string

  overhead: CoatingDeckCoveringSection
  bulkheads: CoatingDeckCoveringSection
  stiffeners: CoatingDeckCoveringSection
  deckBilge: CoatingDeckCoveringSection

  cathodicProtectionInCompartment: string
  totalAnodes: number | null
  anodesDepletedCount: number | null

  insulationOverhead: InsulationSection
  insulationBulkheads: InsulationSection
  insulationStiffeners: InsulationSection
  insulationDeckBilge: InsulationSection

  cracksFracturesPresent: boolean
  bucklingDeflectionsDistortionsPresent: boolean
  holesPresent: boolean
  excessivePittingPresent: boolean
  looseRustScaleExfoliation: boolean
  weldsDeteriorated: boolean
  trippedStiffeners: boolean
  deteriorationToEquipmentFoundation: boolean
  bimetalicStripsDamaged: boolean
  structuralIntegrityRequiresEngineeringEvaluation: boolean
  estimatedTotalLinearFeetOfStructureRequiringRepair: number | null
  estimatedTotalSquareFeetOfPlatingRequiringRepair: number | null

  pictureTakenQuantity: number | null
  pictureNumbers: string

  comments: string

  constructor() {
    this.inspectorName = ''
    this.organization = ''
    this.contactInfo = ''
    this.uic = ''
    this.shipClass = ''
    this.shipName = ''
    this.hull = ''
    this.compartmentNumber = ''
    this.compartmentName = ''
    this.swlin = ''
    this.paintedSurfaceArea = null
    this.wc = ''
    this.access = ''
    this.apl = ''
    this.rin = ''
    this.configCode = ''
    this.configSource = ''
    this.date = ''
    this.assessmentReason = 'SCHEDULED'

    this.overhead = {
      notApplicable: false,
      percentCoatedStructureVisible: null,
      paintCondition: '',
      percentCorrosion: null,
      corrosionLocalOrScattered: '',
      squareFootLocalCorrosion: null,
      coveringDamaged: '',
    }
    this.bulkheads = {
      notApplicable: false,
      percentCoatedStructureVisible: null,
      paintCondition: '',
      percentCorrosion: null,
      corrosionLocalOrScattered: '',
      squareFootLocalCorrosion: null,
      coveringDamaged: '',
    }
    this.stiffeners = {
      notApplicable: false,
      percentCoatedStructureVisible: null,
      paintCondition: '',
      percentCorrosion: null,
      corrosionLocalOrScattered: '',
      squareFootLocalCorrosion: null,
      coveringDamaged: '',
    }
    this.deckBilge = {
      notApplicable: false,
      percentCoatedStructureVisible: null,
      paintCondition: '',
      percentCorrosion: null,
      corrosionLocalOrScattered: '',
      squareFootLocalCorrosion: null,
      coveringDamaged: '',
    }

    this.cathodicProtectionInCompartment = ''
    this.totalAnodes = null
    this.anodesDepletedCount = null

    this.insulationOverhead = {
      insulationPresent: false,
      insulationDamaged: false,
    }
    this.insulationBulkheads = {
      insulationPresent: false,
      insulationDamaged: false,
    }
    this.insulationStiffeners = {
      insulationPresent: false,
      insulationDamaged: false,
    }
    this.insulationDeckBilge = {
      insulationPresent: false,
      insulationDamaged: false,
    }

    this.cracksFracturesPresent = false
    this.bucklingDeflectionsDistortionsPresent = false
    this.holesPresent = false
    this.excessivePittingPresent = false
    this.looseRustScaleExfoliation = false
    this.weldsDeteriorated = false
    this.trippedStiffeners = false
    this.deteriorationToEquipmentFoundation = false
    this.bimetalicStripsDamaged = false
    this.structuralIntegrityRequiresEngineeringEvaluation = false
    this.estimatedTotalLinearFeetOfStructureRequiringRepair = null
    this.estimatedTotalSquareFeetOfPlatingRequiringRepair = null

    this.pictureTakenQuantity = null
    this.pictureNumbers = ''

    this.comments = ''
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  static fromJSON(json: any): G1N6 {
    const g1n6 = new G1N6()

    if (json.inspectorName !== undefined)
      g1n6.inspectorName = json.inspectorName
    if (json.organization !== undefined) g1n6.organization = json.organization
    if (json.contactInfo !== undefined) g1n6.contactInfo = json.contactInfo
    if (json.uic !== undefined) g1n6.uic = json.uic
    if (json.shipClass !== undefined) g1n6.shipClass = json.shipClass
    if (json.shipName !== undefined) g1n6.shipName = json.shipName
    if (json.hull !== undefined) g1n6.hull = json.hull
    if (json.compartmentNumber !== undefined)
      g1n6.compartmentNumber = json.compartmentNumber
    if (json.compartmentName !== undefined)
      g1n6.compartmentName = json.compartmentName
    if (json.swlin !== undefined) g1n6.swlin = json.swlin
    if (json.paintedSurfaceArea !== undefined)
      g1n6.paintedSurfaceArea = json.paintedSurfaceArea
    if (json.wc !== undefined) g1n6.wc = json.wc
    if (json.access !== undefined) g1n6.access = json.access
    if (json.apl !== undefined) g1n6.apl = json.apl
    if (json.rin !== undefined) g1n6.rin = json.rin
    if (json.configCode !== undefined) g1n6.configCode = json.configCode
    if (json.configSource !== undefined) g1n6.configSource = json.configSource
    if (json.date !== undefined) g1n6.date = json.date
    if (json.assessmentReason !== undefined)
      g1n6.assessmentReason = json.assessmentReason

    if (json.overhead !== undefined) g1n6.overhead = json.overhead
    if (json.bulkheads !== undefined) g1n6.bulkheads = json.bulkheads
    if (json.stiffeners !== undefined) g1n6.stiffeners = json.stiffeners
    if (json.deckBilge !== undefined) g1n6.deckBilge = json.deckBilge

    if (json.cathodicProtectionInCompartment !== undefined)
      g1n6.cathodicProtectionInCompartment =
        json.cathodicProtectionInCompartment
    if (json.totalAnodes !== undefined) g1n6.totalAnodes = json.totalAnodes
    if (json.anodesDepletedCount !== undefined)
      g1n6.anodesDepletedCount = json.anodesDepletedCount

    if (json.insulationOverhead !== undefined)
      g1n6.insulationOverhead = json.insulationOverhead
    if (json.insulationBulkheads !== undefined)
      g1n6.insulationBulkheads = json.insulationBulkheads
    if (json.insulationStiffeners !== undefined)
      g1n6.insulationStiffeners = json.insulationStiffeners
    if (json.insulationDeckBilge !== undefined)
      g1n6.insulationDeckBilge = json.insulationDeckBilge

    if (json.cracksFracturesPresent !== undefined)
      g1n6.cracksFracturesPresent = json.cracksFracturesPresent
    if (json.bucklingDeflectionsDistortionsPresent !== undefined)
      g1n6.bucklingDeflectionsDistortionsPresent =
        json.bucklingDeflectionsDistortionsPresent
    if (json.holesPresent !== undefined) g1n6.holesPresent = json.holesPresent
    if (json.excessivePittingPresent !== undefined)
      g1n6.excessivePittingPresent = json.excessivePittingPresent
    if (json.looseRustScaleExfoliation !== undefined)
      g1n6.looseRustScaleExfoliation = json.looseRustScaleExfoliation
    if (json.weldsDeteriorated !== undefined)
      g1n6.weldsDeteriorated = json.weldsDeteriorated
    if (json.trippedStiffeners !== undefined)
      g1n6.trippedStiffeners = json.trippedStiffeners
    if (json.deteriorationToEquipmentFoundation !== undefined)
      g1n6.deteriorationToEquipmentFoundation =
        json.deteriorationToEquipmentFoundation
    if (json.bimetalicStripsDamaged !== undefined)
      g1n6.bimetalicStripsDamaged = json.bimetalicStripsDamaged
    if (json.structuralIntegrityRequiresEngineeringEvaluation !== undefined)
      g1n6.structuralIntegrityRequiresEngineeringEvaluation =
        json.structuralIntegrityRequiresEngineeringEvaluation
    if (json.estimatedTotalLinearFeetOfStructureRequiringRepair !== undefined)
      g1n6.estimatedTotalLinearFeetOfStructureRequiringRepair =
        json.estimatedTotalLinearFeetOfStructureRequiringRepair
    if (json.estimatedTotalSquareFeetOfPlatingRequiringRepair !== undefined)
      g1n6.estimatedTotalSquareFeetOfPlatingRequiringRepair =
        json.estimatedTotalSquareFeetOfPlatingRequiringRepair

    if (json.pictureTakenQuantity !== undefined)
      g1n6.pictureTakenQuantity = json.pictureTakenQuantity
    if (json.pictureNumbers !== undefined)
      g1n6.pictureNumbers = json.pictureNumbers

    if (json.comments !== undefined) g1n6.comments = json.comments

    return g1n6
  }

  static toJSON(g1n6: G1N6): any {
    return {
      inspectorName: g1n6.inspectorName,
      organization: g1n6.organization,
      contactInfo: g1n6.contactInfo,
      uic: g1n6.uic,
      shipClass: g1n6.shipClass,
      shipName: g1n6.shipName,
      hull: g1n6.hull,
      compartmentNumber: g1n6.compartmentNumber,
      compartmentName: g1n6.compartmentName,
      swlin: g1n6.swlin,
      paintedSurfaceArea: g1n6.paintedSurfaceArea,
      wc: g1n6.wc,
      access: g1n6.access,
      apl: g1n6.apl,
      rin: g1n6.rin,
      configCode: g1n6.configCode,
      configSource: g1n6.configSource,
      date: g1n6.date,
      assessmentReason: g1n6.assessmentReason,

      overhead: g1n6.overhead,
      bulkheads: g1n6.bulkheads,
      stiffeners: g1n6.stiffeners,
      deckBilge: g1n6.deckBilge,

      cathodicProtectionInCompartment: g1n6.cathodicProtectionInCompartment,
      totalAnodes: g1n6.totalAnodes,
      anodesDepletedCount: g1n6.anodesDepletedCount,

      insulationOverhead: g1n6.insulationOverhead,
      insulationBulkheads: g1n6.insulationBulkheads,
      insulationStiffeners: g1n6.insulationStiffeners,
      insulationDeckBilge: g1n6.insulationDeckBilge,

      cracksFracturesPresent: g1n6.cracksFracturesPresent,
      bucklingDeflectionsDistortionsPresent:
        g1n6.bucklingDeflectionsDistortionsPresent,
      holesPresent: g1n6.holesPresent,
      excessivePittingPresent: g1n6.excessivePittingPresent,
      looseRustScaleExfoliation: g1n6.looseRustScaleExfoliation,
      weldsDeteriorated: g1n6.weldsDeteriorated,
      trippedStiffeners: g1n6.trippedStiffeners,
      deteriorationToEquipmentFoundation:
        g1n6.deteriorationToEquipmentFoundation,
      bimetalicStripsDamaged: g1n6.bimetalicStripsDamaged,
      structuralIntegrityRequiresEngineeringEvaluation:
        g1n6.structuralIntegrityRequiresEngineeringEvaluation,
      estimatedTotalLinearFeetOfStructureRequiringRepair:
        g1n6.estimatedTotalLinearFeetOfStructureRequiringRepair,
      estimatedTotalSquareFeetOfPlatingRequiringRepair:
        g1n6.estimatedTotalSquareFeetOfPlatingRequiringRepair,

      pictureTakenQuantity: g1n6.pictureTakenQuantity,
      pictureNumbers: g1n6.pictureNumbers,

      comments: g1n6.comments,
    }
  }
}
